import React, { useState, useEffect } from "react";
import ReusableModal from "../../../../components/Modals/ReusableModal";
import { useGetFacilityTypesQuery } from "../../../../features/community/communityApiSlice";

const CommunityAddModal = ({
  isOpen,
  edit,
  clearInput,
  onUpdate,
  onSave,
  setOpen,
  data,
  setData,
}) => {
  const [errorMsg, setErrorMsg] = useState("");
  const { data: facilityTypesList = [], isLoading: facilityTypesLoading } = useGetFacilityTypesQuery(undefined, {
    skip: !isOpen,
  });

  const facilityTypes = facilityTypesList?.data ?? facilityTypesList ?? [];

  // When editing, sync type_id from facility_type so the dropdown shows the correct selection
  useEffect(() => {
    if (!isOpen || !edit || !data?.facility_type || facilityTypes.length === 0) return;
    if (data.type_id) return; // already set
    const match = facilityTypes.find((t) => t.type === data.facility_type);
    if (match?.type_id) {
      setData({ ...data, type_id: match.type_id });
    }
  }, [isOpen, edit, data?.facility_type, data?.type_id, facilityTypes.length]);

  const handleChange = (e) => {
    const tmpData = { ...data };
    if (e.target.name === "is_referral") {
      tmpData[e.target.name] = e.target.checked;
    } else if (e.target.name === "type_id") {
      const typeId = e.target.value || undefined;
      tmpData.type_id = typeId;
      const selected = facilityTypes.find((t) => String(t.type_id) === String(typeId));
      tmpData.facility_type = selected?.type ?? tmpData.facility_type;
    } else {
      tmpData[e.target.name] = e.target.value;
    }
    setData(tmpData);
  };

  return (
    <ReusableModal
      open={isOpen}
      title={edit ? "Edit community" : "Add new community"}
      onClose={() => {
        setOpen(false);
        clearInput();
      }}
    >
      <div className="flex flex-col items-center min-w-[480px]">
        <div className="h-[1px] w-full bg-primarygrayborder" />
        <div className="flex flex-col gap-4 p-6 overflow-auto max-h-[calc(100vh-200px)]">
          <div className="flex flex-row w-full gap-4">
            <div className="flex flex-col w-full gap-2">
              <label htmlFor="name" className="m-0 text-base font-semibold">
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Community"
                className="w-full p-2 border-[1px] border-primarygray rounded-md outline-none"
                value={data?.name}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="flex flex-row w-full gap-4">
            <div className="flex flex-col w-1/2  gap-2">
              <label htmlFor="street" className="m-0 text-base font-semibold">
                Street
              </label>
              <input
                type="text"
                id="street"
                name="street"
                placeholder="Enter Street"
                className="w-full p-2 border-[1px] border-primarygray rounded-md outline-none"
                value={data?.street}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col w-1/2 gap-2">
              <label htmlFor="city" className="m-0 text-base font-semibold">
                City
              </label>
              <input
                type="text"
                id="city"
                name="city"
                className="w-full p-2 border-[1px] border-primarygray rounded-md outline-none"
                placeholder="Enter City"
                value={data?.city}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="flex flex-row w-full gap-4">
            <div className="flex flex-col w-1/2 gap-2">
              <label htmlFor="state" className="m-0 text-base font-semibold">
                State
              </label>
              <input
                type="text"
                id="state"
                name="state"
                className="w-full p-2 border-[1px] border-primarygray rounded-md outline-none"
                placeholder="Enter State"
                value={data?.state}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col w-1/2 gap-2">
              <label htmlFor="zip" className="m-0 text-base font-semibold">
                Zip
              </label>
              <input
                type="text"
                id="zip"
                name="zip"
                className="w-full p-2 border-[1px] border-primarygray rounded-md outline-none"
                placeholder="Zip code"
                value={data?.zip}
                onChange={handleChange}
              />
            </div>
          </div>
         
          <div className="flex flex-row w-full gap-4">
            <div className="flex flex-col w-full gap-2">
              <label htmlFor="type_id" className="m-0 text-base font-semibold">
                Facility Type
              </label>
              <select
                id="type_id"
                name="type_id"
                className="w-full p-2 border-[1px] border-primarygray rounded-md outline-none bg-white"
                value={data?.facility_type ?? ""}
                onChange={handleChange}
                disabled={facilityTypesLoading}
              >
                <option value="">Select facility type</option>
                {facilityTypes.map((item) => (
                  <option key={item.type} value={item.type}>
                    {item.type}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex flex-row w-full gap-4">
            <div className="flex flex-row w-1/2 gap-2">
              <input
                type="checkbox"
                id="is_referral"
                name="is_referral"
                className="p-2"
                checked={data.is_referral}
                onChange={handleChange}
              />
              <label htmlFor="state" className="m-0 text-base font-semibold">
                Is Referral
              </label>
            </div>
          </div>
        </div>
        {errorMsg && <div className="text-red-500">{errorMsg}</div>}
        <div className="w-full d-flex justify-center mb-2">
          <button
            disabled={
              !data.name ||
              !data.street ||
              !data.city ||
              !data.state ||
              !data.zip
            }
            className="w-75 disabled:opacity-40 disabled:cursor-not-allowed font-semibold p-2 border-[2px] rounded-md bg-subtleblue border-primaryblue text-primaryblue whitespace-nowrap"
            onClick={async () => {
              try {
                if (edit) {
                  await onUpdate();
                } else {
                  await onSave();
                }
                setOpen(false);
                clearInput();
              } catch (error) {
                setErrorMsg(error.message);
              }
            }}
          >
            {edit ? "Save" : "Add"}
          </button>
        </div>
      </div>
    </ReusableModal>
  );
};

export default CommunityAddModal;
